import { API_BASE_URL } from "../api/client";

// How the SPA signs people in is decided at RUN time by the API (GET /api/v1/auth/config, anonymous — ported from Summit
// Engineering), so the same image serves every environment. The persona sign-in is used ONLY when the API says
// "dev-stub" (the API refuses that mode outside Development). The build-time value below is just the fallback when the
// API cannot be asked: a production build (the container image, built with VITE_AUTH_MODE=entra) always falls back to
// "Microsoft sign-in is not configured" — never to the persona stub; only the local development server falls back to it.

export type AuthMode = "dev-persona" | "entra";

/** The configuration the API serves from `GET /auth/config`. No client secret ever exists here — identifiers only. */
export interface SpaAuthConfig {
  mode: AuthMode;
  /** Entra: tenant, SPA client id and API scope are all present, so Microsoft sign-in is offered. */
  configured: boolean;
  tenantId?: string | null;
  clientId?: string | null;
  apiScope?: string | null;
  authority?: string | null;
  /** The MSAL redirect bridge page, relative to the site root (registered as the SPA redirect URI). */
  redirectPath?: string | null;
  /** Why Microsoft sign-in is unavailable, in words a person can act on. */
  unavailableReason?: string;
  /** Release 1.5.0 switch from the API (`Release150:Enabled`); false/absent = behave exactly as 1.4.0. */
  release150?: boolean;
}

/** Build-time fallback. A production build is always "entra"; the persona sign-in can only be the dev server's fallback. */
export const BUILD_AUTH_MODE: AuthMode =
  import.meta.env.PROD || import.meta.env.VITE_AUTH_MODE === "entra" ? "entra" : "dev-persona";

export const ENTRA_NOT_CONFIGURED =
  "Microsoft sign-in is not configured for this environment yet. Ask IT to finish the Summit Operations app registration (infra\\entra\\README.md).";

export function fallbackAuthConfig(mode: AuthMode = BUILD_AUTH_MODE): SpaAuthConfig {
  return mode === "entra"
    ? { mode: "entra", configured: false, tenantId: null, clientId: null, apiScope: null, authority: null, redirectPath: null, unavailableReason: ENTRA_NOT_CONFIGURED }
    : { mode: "dev-persona", configured: true };
}

const text = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

/** Reads the API's answer. Anything unexpected falls back to the build-time mode (never "dev-persona" in production). */
export function normaliseAuthConfig(raw: unknown): SpaAuthConfig {
  const r = (raw ?? {}) as Record<string, unknown>;
  const release150 = r.release150 === true;
  if (r.mode === "dev-stub") return { ...fallbackAuthConfig("dev-persona"), release150 };
  if (r.mode !== "entra") return fallbackAuthConfig();
  const config: SpaAuthConfig = {
    mode: "entra",
    configured: false,
    tenantId: text(r.tenantId),
    clientId: text(r.clientId),
    apiScope: text(r.apiScope),
    authority: text(r.authority),
    redirectPath: text(r.redirectPath) ?? "/redirect.html",
    release150
  };
  config.configured = r.configured === true && !!config.tenantId && !!config.clientId && !!config.apiScope && !!config.authority;
  if (!config.configured) config.unavailableReason = ENTRA_NOT_CONFIGURED;
  return config;
}

/** Reads the run-time sign-in configuration; never throws. */
export async function loadAuthConfig(fetchImpl: typeof fetch = fetch): Promise<SpaAuthConfig> {
  try {
    const response = await fetchImpl(`${API_BASE_URL}/auth/config`, { headers: { Accept: "application/json" }, credentials: "same-origin" });
    if (!response.ok) return fallbackAuthConfig();
    return normaliseAuthConfig(await response.json());
  } catch {
    return fallbackAuthConfig();
  }
}
